import { Suspense } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';

export default function CityOfElPasoBusinessLicensePage() {
  return (
    <>
      <SEOHead
        title="City of El Paso Business License Requirements & Application"
        description="Complete guide to obtaining your City of El Paso business license. Learn about application process, fees, renewal requirements, and industry-specific licenses for El Paso businesses."
        canonical="/regulations/city-of-el-paso-business-license"
        keywords="El Paso business license, City of El Paso license, business permit El Paso, El Paso license fees, El Paso business registration"
        ogImage="https://public.readdy.ai/ai/img_res/81ce4f7a150fb1b0b0dc4161d63432d6.jpg"
      />
      <Suspense fallback={<div>Loading...</div>}>
        <div className="min-h-screen bg-white flex flex-col">
          <Navbar />
          
          <main className="flex-grow pt-24 pb-16">
            <div className="max-w-5xl mx-auto px-6">
              {/* Breadcrumb */}
              <nav aria-label="Breadcrumb" className="mb-8">
                <Link to="/regulations" className="text-sm text-[#2C5F4F] hover:text-[#1B3A3A] font-medium flex items-center space-x-2 cursor-pointer">
                  <i className="ri-arrow-left-line"></i>
                  <span>Back to Regulations</span>
                </Link>
              </nav>

              {/* Breadcrumb Structured Data */}
              <script type="application/ld+json">
                {JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  "itemListElement": [
                    {
                      "@type": "ListItem",
                      "position": 1,
                      "name": "Home",
                      "item": "https://elpasotexasbusiness.com/"
                    },
                    {
                      "@type": "ListItem",
                      "position": 2,
                      "name": "Regulations",
                      "item": "https://elpasotexasbusiness.com/regulations"
                    },
                    {
                      "@type": "ListItem",
                      "position": 3,
                      "name": "City of El Paso Business License",
                      "item": "https://elpasotexasbusiness.com/regulations/city-of-el-paso-business-license"
                    }
                  ]
                })}
              </script>

              {/* Header */}
              <div className="mb-12">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#1B3A3A] to-[#2C5F4F] rounded-2xl flex items-center justify-center">
                    <i className="ri-building-line text-white text-3xl"></i>
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold text-gray-900">City of El Paso Business License</h1>
                    <p className="text-lg text-gray-600 mt-2">Local business licensing information and requirements</p>
                  </div>
                </div>
              </div>

              {/* Official Website Link */}
              <div className="bg-gradient-to-br from-[#1B3A3A] to-[#2C5F4F] rounded-2xl p-8 mb-12">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Official Website</h3>
                    <p className="text-white/80">Visit the City of El Paso for business license applications</p>
                  </div>
                  <a
                    href="https://www.elpasotexas.gov/development-services"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-[#1B3A3A] px-8 py-3 rounded-xl font-semibold hover:bg-[#FFE8D6] transition-all cursor-pointer flex items-center space-x-2 whitespace-nowrap"
                  >
                    <span>Visit ElPasoTexas.gov</span>
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>

              {/* Overview */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Business License Overview</h2>
                <div className="bg-gray-50 rounded-2xl p-8">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Most businesses operating within El Paso city limits are required to obtain a business license from the City of El Paso. This license ensures your business complies with local regulations and zoning requirements.
                  </p>
                  
                  <div className="bg-[#FFE8D6] rounded-xl p-6">
                    <p className="text-sm text-gray-800">
                      <strong>Important:</strong> A City of El Paso business license is separate from your state business registration with the Texas Secretary of State. You may need both to operate legally.
                    </p>
                  </div>
                </div>
              </section>

              {/* Application Process */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">License Application Process</h2>
                <div className="bg-gray-50 rounded-2xl p-8">
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">1</span>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Determine License Type</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Identify which type of business license you need based on your business activities. Common types include retail, food service, home-based business, contractor, and professional services licenses.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">2</span>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Gather Required Documents</h3>
                        <p className="text-gray-700 leading-relaxed mb-3">
                          Collect all necessary documentation before submitting your application:
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-start space-x-2">
                            <i className="ri-checkbox-circle-fill text-[#2C5F4F] mt-1"></i>
                            <span className="text-gray-700">Texas Certificate of Formation or registration documents</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <i className="ri-checkbox-circle-fill text-[#2C5F4F] mt-1"></i>
                            <span className="text-gray-700">Federal Employer Identification Number (EIN)</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <i className="ri-checkbox-circle-fill text-[#2C5F4F] mt-1"></i>
                            <span className="text-gray-700">Texas Sales Tax Permit (if applicable)</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <i className="ri-checkbox-circle-fill text-[#2C5F4F] mt-1"></i>
                            <span className="text-gray-700">Proof of property ownership or lease agreement</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <i className="ri-checkbox-circle-fill text-[#2C5F4F] mt-1"></i>
                            <span className="text-gray-700">Certificate of Occupancy (for physical locations)</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <i className="ri-checkbox-circle-fill text-[#2C5F4F] mt-1"></i>
                            <span className="text-gray-700">Professional licenses or certifications (if required)</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">3</span>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Submit Application</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Complete the business license application online through the City of El Paso Development Services portal or submit in person at the Development Services Department located at 800 N. Campbell St., El Paso, TX 79901.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">4</span>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Pay License Fee</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Pay the required license fee based on your business type and size. Fees typically range from $50 to $500 annually. Payment can be made online, by mail, or in person.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">5</span>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Inspection & Approval</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Depending on your business type, you may need to pass inspections from the Fire Department, Health Department, or Building Inspection. Once approved, your license will be issued within 5-10 business days.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">6</span>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Display Your License</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Once received, display your business license prominently at your business location. Keep a copy for your records and be prepared to show it during inspections.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* License Fees */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">License Fees by Business Type</h2>
                <div className="bg-gray-50 rounded-2xl p-8">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Business license fees vary based on the type of business and number of employees. Below are typical fee ranges:
                  </p>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-gray-300">
                          <th className="text-left py-3 px-4 font-bold text-gray-900">Business Type</th>
                          <th className="text-left py-3 px-4 font-bold text-gray-900">Annual Fee</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        <tr>
                          <td className="py-3 px-4 text-gray-700">Home-Based Business (no employees)</td>
                          <td className="py-3 px-4 text-gray-700">$50</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 text-gray-700">Retail Store (1-5 employees)</td>
                          <td className="py-3 px-4 text-gray-700">$100</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 text-gray-700">Restaurant or Food Service</td>
                          <td className="py-3 px-4 text-gray-700">$150-$300</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 text-gray-700">Professional Services (6-20 employees)</td>
                          <td className="py-3 px-4 text-gray-700">$200</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 text-gray-700">Contractor or Construction</td>
                          <td className="py-3 px-4 text-gray-700">$250</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 text-gray-700">Large Business (20+ employees)</td>
                          <td className="py-3 px-4 text-gray-700">$300-$500</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-6 bg-[#FFE8D6] rounded-xl p-6">
                    <p className="text-sm text-gray-800">
                      <strong>Note:</strong> Additional fees may apply for specific industries such as alcohol sales, tobacco sales, or businesses requiring special permits.
                    </p>
                  </div>
                </div>
              </section>

              {/* Renewal Process */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">License Renewal</h2>
                <div className="bg-gray-50 rounded-2xl p-8">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Business licenses in El Paso must be renewed annually. The City will send renewal notices 30-60 days before your license expires.
                  </p>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Renewal Requirements</h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700">Submit renewal application before expiration date</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700">Pay renewal fee (same as initial license fee)</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700">Update any changes to business information</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700">Provide proof of current insurance (if required)</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700">Pass any required re-inspections</span>
                    </li>
                  </ul>
                  
                  <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-6">
                    <p className="text-sm text-red-800">
                      <strong>Warning:</strong> Operating with an expired license can result in fines up to $500 per day and potential business closure. Late renewals may incur additional penalties.
                    </p>
                  </div>
                </div>
              </section>

              {/* Industry-Specific Licenses */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Industry-Specific Licenses</h2>
                <div className="bg-gray-50 rounded-2xl p-8">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Certain industries require additional specialized licenses or permits beyond the standard business license:
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <h4 className="font-bold text-gray-900 mb-2 flex items-center space-x-2">
                        <i className="ri-restaurant-line text-[#2C5F4F]"></i>
                        <span>Food Service</span>
                      </h4>
                      <p className="text-sm text-gray-600">Health permit, food handler certifications, kitchen inspections</p>
                    </div>
                    
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <h4 className="font-bold text-gray-900 mb-2 flex items-center space-x-2">
                        <i className="ri-beer-line text-[#2C5F4F]"></i>
                        <span>Alcohol Sales</span>
                      </h4>
                      <p className="text-sm text-gray-600">TABC license, seller training certification, local alcohol permit</p>
                    </div>
                    
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <h4 className="font-bold text-gray-900 mb-2 flex items-center space-x-2">
                        <i className="ri-hammer-line text-[#2C5F4F]"></i>
                        <span>Contractors</span>
                      </h4>
                      <p className="text-sm text-gray-600">State contractor license, liability insurance, building permits</p>
                    </div>
                    
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <h4 className="font-bold text-gray-900 mb-2 flex items-center space-x-2">
                        <i className="ri-scissors-line text-[#2C5F4F]"></i>
                        <span>Personal Care</span>
                      </h4>
                      <p className="text-sm text-gray-600">Cosmetology license, health inspection, sanitation compliance</p>
                    </div>
                    
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <h4 className="font-bold text-gray-900 mb-2 flex items-center space-x-2">
                        <i className="ri-car-line text-[#2C5F4F]"></i>
                        <span>Auto Services</span>
                      </h4>
                      <p className="text-sm text-gray-600">Automotive facility license, environmental permits, mechanic certifications</p>
                    </div>
                    
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <h4 className="font-bold text-gray-900 mb-2 flex items-center space-x-2">
                        <i className="ri-home-heart-line text-[#2C5F4F]"></i>
                        <span>Home-Based Business</span>
                      </h4>
                      <p className="text-sm text-gray-600">Zoning approval, HOA compliance, parking restrictions</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Contact Information */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="bg-gray-50 rounded-2xl p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-bold text-gray-900 mb-3">Development Services Department</h3>
                      <p className="text-sm text-gray-700 mb-2">Phone: (915) 212-0065</p>
                      <p className="text-sm text-gray-700 mb-2">Hours: Monday-Friday, 8:00 AM - 5:00 PM</p>
                      <p className="text-sm text-gray-700">Email: developmentservices@elpasotexas.gov</p>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-3">Office Location</h3>
                      <p className="text-sm text-gray-700">
                        Development Services Department<br />
                        800 N. Campbell St.<br />
                        El Paso, TX 79901
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* CTA Section */}
              <div className="bg-gradient-to-br from-[#1B3A3A] to-[#2C5F4F] rounded-3xl p-12 text-center">
                <h2 className="text-4xl font-bold text-white mb-4">Need Help with Your Business License?</h2>
                <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                  We can help you navigate the El Paso business licensing process and ensure you have all required permits.
                </p>
                <Link
                  to="/contact"
                  className="inline-block bg-white text-[#1B3A3A] px-10 py-4 rounded-xl text-lg font-semibold hover:bg-[#FFE8D6] transition-all cursor-pointer whitespace-nowrap"
                >
                  Get Licensing Assistance
                </Link>
              </div>
            </div>
          </main>

          <Footer />
        </div>
      </Suspense>
    </>
  );
}